/** @fileoverview Coordinates app creation, editing, and preview routes. */

import { useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppBuilderWorkspace from '../components/AppBuilder/AppBuilderWorkspace';
import JsonCanvasEditor from '../components/AppBuilder/JsonCanvasEditor';
import { initialDefinitionJson, parseDefinition } from '../components/AppBuilder/definition';
import { createFabrikApp } from '../services/appsApi';
import type { Tenant, User } from '../types/identity';
import type { FabrikAppDefinition } from '../types/apps';

type Props = {
  tenant: Tenant;
  user?: User;
  appId?: string;
  setChatPortalTarget: Dispatch<SetStateAction<HTMLElement | null>>;
  setPortalChatOptions: Dispatch<
    SetStateAction<{
      previewOpen: boolean;
      onPreviewToggle: () => void;
      onAppDraftUpdated: (definition: FabrikAppDefinition) => void;
    } | null>
  >;
};

export default function AppBuilderPage({
  tenant,
  user,
  appId,
  setChatPortalTarget,
  setPortalChatOptions,
}: Props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tenantId = tenant.tenant_id;
  const isCanvasMode = searchParams.get('mode') === 'canvas';
  const [definition, setDefinition] = useState(initialDefinitionJson);
  const [savedAppId, setSavedAppId] = useState(appId);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const parsed = useMemo(() => parseDefinition(definition), [definition]);

  const save = async () => {
    if (!parsed.definition || parsed.error) return;
    setSaving(true);
    setSaveMessage('');
    setSaveError('');

    try {
      const app = await createFabrikApp(tenantId, parsed.definition);
      setSavedAppId(app.id);
      setSaveMessage('Saved');
      navigate(`/t/${encodeURIComponent(tenantId)}/apps/${encodeURIComponent(app.id)}`, {
        replace: true,
      });
    } catch (reason: unknown) {
      setSaveError(reason instanceof Error ? reason.message : 'Unable to save this app.');
    } finally {
      setSaving(false);
    }
  };

  if (savedAppId) {
    return (
      <AppBuilderWorkspace
        key={savedAppId}
        tenant={tenant}
        user={user}
        appId={savedAppId}
        setChatPortalTarget={setChatPortalTarget}
        setPortalChatOptions={setPortalChatOptions}
        onBack={() => navigate(`/t/${encodeURIComponent(tenantId)}`)}
      />
    );
  }

  if (!isCanvasMode) return null;

  return (
    <JsonCanvasEditor
      definition={definition}
      jsonError={parsed.error}
      saveError={saveError}
      saveMessage={saveMessage}
      saving={saving}
      onDefinitionChange={(value) => {
        setDefinition(value);
        setSaveMessage('');
        setSaveError('');
      }}
      onReset={() => setDefinition(initialDefinitionJson)}
      onSave={() => void save()}
    />
  );
}
